import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { finalize, flatMap, takeUntil, tap } from 'rxjs/operators';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LoaderService } from 'src/app/services/loader.service';
import { OpfoelgningService, ServiceAftaleService } from 'src/app/api/services';
import { defer, Subject } from 'rxjs';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { GetServiceaftalerRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/get-serviceaftaler-request';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { OpfoelgningAction, OpfoelgningDialogData, OpfoelgningDialogResponse, ServiceaftaleFilter } from '../models';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { GetServiceaftalerResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/get-serviceaftaler-response';
import { AuthService } from 'src/app/services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { OpfoelgningDialogComponent } from 'src/app/opfoelgning/components/opfoelgning-dialog/opfoelgning-dialog.component';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { OpfoelgningChanged, SignalrService } from 'src/app/services/signalr.service';
import { PaginationCacheService } from 'src/app/services/paginationcache.service';
import { GetServiceaftaleResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/get-serviceaftale-response';
import { OpfoelgningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/opfoelgning-dto';
import { inOutAnimation } from 'src/app/common/animations';
import { ServiceaftaleStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceaftale-state-enum';
import { ExcelDownloadDialogComponent } from '../../shared/excel/excel-download-dialog/excel-download-dialog.component';
import { ExcelDownloadDialogModel } from '../../shared/excel/excel-download-dialog/excel-download-dialog.model';
import { ExcelDocumentTypeEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/excel-document-type-enum';
import { ExcelDocumentHandlerTypeEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/excel-document-handler-type-enum';
import { OpfoelgningerService } from 'src/app/services/opfoelgninger.service';

@Component({
  selector: 'app-list',
  templateUrl: './aftale-list.component.html',
  styleUrls: ['./aftale-list.component.scss'],
  animations: [inOutAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AftaleListComponent implements OnInit, OnDestroy, AfterViewInit {
  rowCount: number;
  pageCount: number;

  filterKey: string = 'aftale-filter';

  filter: ServiceaftaleFilter = {
    productIds: ([] = []),
    serviceaftaleStateIds: ([] = []),
    serviceaftaleTagIds: ([] = []),
    partnerNos: ([] = []),
    orderBy: 'Ajourtid',
    orderDir: 'desc',
  };

  searchQuery: string;

  isAdmin: boolean = false;
  canWrite: boolean = false;

  quickViewOpen: boolean = false;
  globalActiveRowID: string = '';
  serviceaftaler: ServiceaftaleDto[] = [];
  selectedaftale: ServiceaftaleDto;
  displayedColumns: string[] = [
    'Serviceabonnement.Produkt',
    'actions',
    'Partner.Name',
    'ServiceaftaleState',
    'OkKundeAftaleNr',
    'kundeNavn',
    'adresse',
    'telefon',
    'Ajourtid',
    'Oprettid',
  ];
  unsubscribe$ = new Subject<void>();

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.quickViewOpen) {
        this.closeQuickView();
      }
    }
  }

  dataSource: any;

  constructor(
    private serviceAftaleService: ServiceAftaleService,
    private router: Router,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private localStorage: LocalStorageService,
    private auth: AuthService,
    private dialog: MatDialog,
    private opfoelgningService: OpfoelgningService,
    private notification: NotificationService,
    private signalR: SignalrService,
    private paginationCache: PaginationCacheService,
    private opfoelgningerService: OpfoelgningerService,
  ) {
    this.dataSource = new MatTableDataSource(this.serviceaftaler);
    this.isAdmin = this.auth.isAdministrator;
    this.canWrite = this.auth.canWrite;
  }

  @ViewChild(MatSort) sort: MatSort;

  ngOnInit(): void {
    this.searchQuery = null;

    this.serviceaftaler = [];

    this.route.queryParams
      .pipe(
        flatMap((queryParams: Params) => {
          const filter: ServiceaftaleFilter = {
            productIds: ([] = []),
            serviceaftaleStateIds: ([] = []),
            serviceaftaleTagIds: ([] = []),
            partnerNos: ([] = []),
            orderBy: 'Ajourtid',
            orderDir: 'desc',
          };

          const localFilter = this.localStorage.get(this.filterKey);

          if (localFilter) {
            filter.orderBy = localFilter.orderBy;
            filter.orderDir = localFilter.orderDir;
            filter.productIds = localFilter.productIds ?? [];
            filter.serviceaftaleStateIds = localFilter.serviceaftaleStateIds ?? [];
            filter.partnerNos = localFilter.partnerNos ?? [];
          }

          this.searchQuery = '';

          const q = queryParams['q'];
          const serviceaftaleTagIds = queryParams['serviceaftaleTagIds'];
          const serviceaftaleStateIds = queryParams['serviceaftaleStateIds'];
          const partnerNos = queryParams['partnerNos'];
          const productIds = queryParams['productIds'];
          const orderBy = queryParams['orderBy'];
          const orderDir = queryParams['orderDir'];

          if (q) {
            this.searchQuery = q;
          }

          if (orderBy && orderBy != '') {
            filter.orderBy = orderBy;
          }

          if (orderDir && orderDir != '') {
            filter.orderDir = orderDir;
          }

          if (serviceaftaleTagIds) {
            if (serviceaftaleTagIds instanceof Array) {
              filter.serviceaftaleTagIds = serviceaftaleTagIds.map(function (x: string) {
                return Number(x);
              });
            } else {
              filter.serviceaftaleTagIds.push(Number(serviceaftaleTagIds));
            }
          }

          if (serviceaftaleStateIds) {
            if (serviceaftaleStateIds instanceof Array) {
              filter.serviceaftaleStateIds = serviceaftaleStateIds;
            } else {
              filter.serviceaftaleStateIds.push(serviceaftaleStateIds);
            }
          }

          if (partnerNos) {
            if (partnerNos instanceof Array) {
              filter.partnerNos = partnerNos.map(function (x: string) {
                return Number(x);
              });
            } else {
              filter.partnerNos.push(Number(partnerNos));
            }
          }

          if (productIds) {
            if (productIds instanceof Array) {
              filter.productIds = productIds;
            } else {
              filter.productIds.push(productIds);
            }
          }
          filter.productIds = filter.productIds.filter((v, i, a) => a.indexOf(v) === i);
          filter.serviceaftaleStateIds = filter.serviceaftaleStateIds.filter((v, i, a) => a.indexOf(v) === i);
          filter.partnerNos = filter.partnerNos.filter((v, i, a) => a.indexOf(v) === i);

          filter.serviceaftaleTagIds = filter.serviceaftaleTagIds.filter((v, i, a) => a.indexOf(v) === i);

          this.localStorage.set(this.filterKey, filter);

          this.filter = filter;

          const pagination = this.paginationCache.position;
          if (pagination) {
            this.page = Number(pagination.page);
            //If page not valid - we start from beginning
            if (Number.isNaN(this.page)) {
              this.page = 1;
            }
            this.prevRow = pagination.id;
          }

          return this.getServiceaftaler$.pipe(
            tap((response: GetServiceaftalerResponse) => {
              this.skip = this.page - 1;
              this.rowCount = response.rowCount;
              this.pageCount = response.pageCount;
              this.serviceaftaler = this.serviceaftaler = response.serviceaftaler;
              this.dataSource = new MatTableDataSource(this.serviceaftaler);
              if (response.errorCode && response.errorCode === 900001) {
                this.errorHandler.showG4Error();
              }
            }),
          );
        }),
      )
      .subscribe(
        () => {},
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
          this.serviceaftaler = [];
          this.dataSource = new MatTableDataSource(this.serviceaftaler);
        },
      );

    this.signalR.opfoelgningChanged$.pipe(takeUntil(this.unsubscribe$)).subscribe((event: OpfoelgningChanged) => {
      const serviceaftale = this.serviceaftaler.find((x) => x.serviceAftaleNo == event.entityNo);
      if (serviceaftale) {
        //TODO: [thomas] Update opfoelgning
        //serviceaftale.opfoelgning = event.opfoelgning;

        let index = serviceaftale.opfoelgningList.findIndex((p) => p.id == event.opfoelgning.id);
        if (index >= 0) serviceaftale.opfoelgningList.splice(index, 1, event.opfoelgning);
        else serviceaftale.opfoelgningList.push(event.opfoelgning);

        this.dataSource = new MatTableDataSource(this.serviceaftaler);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  getServiceaftaler$ = defer(() => {
    this.loader.show();

    let top = this.page - this.skip;
    let skip = this.skip > this.page ? top - 1 : this.skip;
    const request: GetServiceaftalerRequest = {
      pageTop: top < 1 ? 1 : top,
      pageSkip: skip < 0 ? 0 : skip,
      searchQuery: this.searchQuery,
      productIds: this.filter.productIds,
      serviceaftaleStateIds: this.filter.serviceaftaleStateIds,
      serviceaftaleTagIds: this.filter.serviceaftaleTagIds,
      partnerNos: this.filter.partnerNos,
      orderBy: this.filter.orderBy,
      orderDir: this.filter.orderDir,
    };

    return this.serviceAftaleService.getServiceaftaler({ caller: EGIS_SERVICE_CALLER, body: request }).pipe(
      tap((response) => {
        this.rowCount = response.rowCount;
        this.pageCount = response.pageCount;
        this.serviceaftaler = this.serviceaftaler.concat(response.serviceaftaler);
        this.dataSource = new MatTableDataSource(this.serviceaftaler);
      }),
      finalize(() => {
        this.loader.hide();
      }),
      takeUntil(this.unsubscribe$),
    );
  });

  onRowClick(row: ServiceaftaleDto) {
    this.globalActiveRowID = row.id.toString();
    if (this.searchQuery) {
      this.serviceAftaleService
        .getServiceAftaleForKunde({
          caller: EGIS_SERVICE_CALLER,
          okKundeAftaleNr: row.okKundeAftaleNr,
        })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (response: GetServiceaftaleResponse) => {
            this.selectedaftale = response.serviceaftale;
            this.quickViewOpen = true;
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    } else {
      this.quickViewOpen = true;
      this.selectedaftale = row;
    }
  }

  onRowDblClick(row: ServiceaftaleDto) {
    this.router.navigate(['/aftale', row.okKundeAftaleNr, { page: this.page }]);
  }

  getProductIcon(serviceAftale: ServiceaftaleDto) {
    return serviceAftale && serviceAftale.serviceAbonnement && serviceAftale.serviceAbonnement.product
      ? serviceAftale.serviceAbonnement.product.text.toLowerCase()
      : '';
  }

  getStatusIcon(serviceAftale: ServiceaftaleDto) {
    if (serviceAftale.isActive) return 'checkmark';
    //Created-pending vs notactive
    return serviceAftale.serviceaftaleState.state == ServiceaftaleStateEnum.CreatedPending ? 'watch' : 'ban';
  }

  closeQuickView() {
    this.quickViewOpen = false;
    this.globalActiveRowID = '';
  }

  more() {
    this.page += 1;
    this.skip += 1;
    this.loader.show();
    (this.getServiceaftaler$
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe(),
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
        this.page -= 1;
      });
  }

  sortData(sort: Sort) {
    this.serviceaftaler = [];

    const orderParams: Params = {
      orderBy: sort.active ? sort.active : null,
      orderDir: sort.direction ? sort.direction : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: orderParams,
      queryParamsHandling: 'merge',
    });
  }

  opfoelgning(event: Event, serviceaftale: ServiceaftaleDto) {
    event.stopPropagation();

    const data: OpfoelgningDialogData = {
      entityNo: serviceaftale.serviceAftaleNo,
      opfoelgninger: serviceaftale.opfoelgningList,
      okKundeAftaleNr: serviceaftale.okKundeAftaleNr,
    };
    let dialogRef = this.dialog.open(OpfoelgningDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data,
    });

    dialogRef.afterClosed().subscribe((response: OpfoelgningDialogResponse) => {
      if (response) {
        this.onOpfoelgningChanged(response);
      }
    });
  }

  editSlutOpgoerelse(event: Event, serviceaftale: ServiceaftaleDto) {
    event.stopPropagation();
  }

  concatAdresseLokation(serviceaftale: ServiceaftaleDto): string {
    const adresse =
      serviceaftale.kunde?.anlaegsadresse?.adressLine == null || serviceaftale.kunde?.anlaegsadresse?.adressLine == undefined
        ? null
        : serviceaftale.kunde?.anlaegsadresse?.adressLine;
    const lokation =
      serviceaftale.kunde?.lokation == null || serviceaftale.kunde?.lokation == undefined ? null : serviceaftale.kunde?.lokation;

    if (adresse && lokation) return `${adresse}\u00A0\u00B7\u00A0${lokation}`;
    if (adresse && !lokation) return adresse;
    if (!adresse && lokation) return lokation;
    return 'N/A';
  }

  onOpfoelgningChanged(response: OpfoelgningDialogResponse) {
    if (response.action == OpfoelgningAction.Upsert) {
      this.loader.show();

      this.opfoelgningService
        .upsertServiceaftaleOpfoelgning({
          caller: EGIS_SERVICE_CALLER,
          body: {
            id: response.opfoelgning.id,
            note: response.opfoelgning.note,
            deadline: response.opfoelgning.deadline ? new Date(response.opfoelgning.deadline).toOkDateString() : null,
            prioritet: response.opfoelgning.priority,
            entityNo: response.opfoelgning.entityNo,
          },
        })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er gemt.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    } else if (response.action == OpfoelgningAction.Delete) {
      this.opfoelgningService
        .removeServiceaftaleOpfoelgning({ caller: EGIS_SERVICE_CALLER, id: response.opfoelgning.id })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er fjernet.');
            const sa = this.serviceaftaler.find((x) => x.serviceAftaleNo == response.opfoelgning.entityNo);
            if (sa) {
              let index = sa.opfoelgningList.findIndex((p) => p.id == response.opfoelgning.id);
              if (index >= 0) sa.opfoelgningList.splice(index, 1);
            }
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    }
  }

  skip: number = 0;
  page: number = 1;
  prevRow: number = null;

  highLight(id: number) {
    var row = this.serviceaftaler.find((p) => p.okKundeAftaleNr == id);
    if (row) this.globalActiveRowID = row.id.toString();
  }

  opfoelgninger(dto: ServiceaftaleDto): string {
    return this.useropfolgninger(dto)
      .map((o) => o.note)
      .join('\n');
  }

  useropfolgninger(dto: ServiceaftaleDto): Array<OpfoelgningDto> {
    return dto.opfoelgningList.filter((p) => p.user == this.auth.username);
  }

  button(serviceaftale) {
    return this.opfoelgningerService.serviceaftaleState(serviceaftale, this.auth.username);
  }

  download() {
    const request: GetServiceaftalerRequest = {
      searchQuery: this.searchQuery,
      productIds: this.filter.productIds,
      serviceaftaleStateIds: this.filter.serviceaftaleStateIds,
      serviceaftaleTagIds: this.filter.serviceaftaleTagIds,
      partnerNos: this.filter.partnerNos,
      orderBy: this.filter.orderBy,
      orderDir: this.filter.orderDir,
    };

    const data: ExcelDownloadDialogModel = {
      documentType: ExcelDocumentTypeEnum.Serviceaftaler,
      handlerType: ExcelDocumentHandlerTypeEnum.Default,
      filter: JSON.stringify(request),
    };
    this.dialog.open(ExcelDownloadDialogComponent, {
      data: data,
    });
  }
}
